import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Fuse from 'fuse.js';

interface SearchMatch {
  lineIndex: number;
  lineText: string;
  headingId: string | null;
}

interface StandardsSearchBarProps {
  markdownText: string;
}

function findNearestHeading(lines: string[], targetIndex: number): string | null {
  for (let i = targetIndex; i >= 0; i--) {
    const m = lines[i].match(/^#{1,3}\s+(.+)$/);
    if (m) {
      const text = m[1].replace(/\*\*|__|`/g, '').trim();
      return text
        .toLowerCase()
        .replace(/[^\w\s가-힣-]/g, '')
        .replace(/[\s_]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }
  }
  return null;
}

export function StandardsSearchBar({ markdownText }: StandardsSearchBarProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchMatch[]>([]);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const fuseRef = useRef<Fuse<{ idx: number; text: string }> | null>(null);
  const linesRef = useRef<string[]>([]);

  useEffect(() => {
    const lines = markdownText.split('\n');
    linesRef.current = lines;
    const docs = lines.map((text, idx) => ({ idx, text }));
    fuseRef.current = new Fuse(docs, {
      keys: ['text'],
      threshold: 0.3,
      includeScore: true,
    });
  }, [markdownText]);

  const handleSearch = useCallback((q: string) => {
    setQuery(q);
    if (!q.trim() || !fuseRef.current) {
      setResults([]);
      setOpen(false);
      return;
    }
    const raw = fuseRef.current.search(q).slice(0, 10);
    const matches: SearchMatch[] = raw.map((r) => ({
      lineIndex: r.item.idx,
      lineText: r.item.text,
      headingId: findNearestHeading(linesRef.current, r.item.idx),
    }));
    setResults(matches);
    setOpen(matches.length > 0);
  }, []);

  const handleSelect = (match: SearchMatch) => {
    if (match.headingId) {
      const el = document.getElementById(match.headingId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setOpen(false);
    setQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <div
      style={{ position: 'relative', marginBottom: 'var(--spacing-4)' }}
      role="search"
    >
      <input
        ref={inputRef}
        type="search"
        value={query}
        onChange={(e) => handleSearch(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={t('devStandardsViewer.search.placeholder')}
        aria-label={t('devStandardsViewer.search.placeholder')}
        aria-expanded={open}
        aria-autocomplete="list"
        style={{
          width: '100%',
          padding: 'var(--spacing-2) var(--spacing-3)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-sm)',
          fontFamily: 'var(--font-family-sans)',
          background: 'var(--color-surface-background)',
          color: 'var(--color-text-primary)',
          outline: 'none',
        }}
      />
      {open && results.length > 0 && (
        <ul
          role="listbox"
          aria-label={t('devStandardsViewer.search.placeholder')}
          style={{
            position: 'absolute',
            zIndex: 100,
            top: '100%',
            left: 0,
            right: 0,
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.16)',
            listStyle: 'none',
            margin: 0,
            padding: 'var(--spacing-1)',
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {results.map((match, idx) => (
            <li key={idx} role="option" aria-selected={false}>
              <button
                type="button"
                onClick={() => handleSelect(match)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-xs)',
                  fontFamily: 'var(--font-family-sans)',
                  color: 'var(--color-text-primary)',
                  borderRadius: 'var(--radius-sm)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'var(--color-surface-muted)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                {match.lineText.slice(0, 100)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
